import { useEffect, useState } from "react";
import { Quote, Trash2, Eye, EyeOff, Plus } from "lucide-react";
import { toast } from "sonner";
import { adminApi, apiError } from "@/lib/adminApi";

const FIELD = "w-full bg-transparent border-b border-[#C9BFA8] focus:border-[#8a6b2f] outline-none py-3 text-navy placeholder:text-ink-muted/60 transition-colors";
const LABEL = "font-mono text-[10px] uppercase tracking-[0.24em] text-ink";
const EMPTY = { quote: "", author: "", business: "", published: true };

const TestimonialRow = ({ t, onToggle, onDelete }) => (
  <li data-testid="admin-testimonial-item" data-published={t.published ? "true" : "false"} className="py-5 grid sm:grid-cols-12 gap-4 items-start">
    <div className="sm:col-span-9">
      <p className={`font-serif text-lg leading-snug ${t.published ? "text-navy" : "text-ink-muted"}`}>“{t.quote}”</p>
      <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">
        <span className="text-navy">{t.author}</span>{t.business && <> · {t.business}</>}
        {!t.published && <span className="ml-3 text-[#8a6b2f]">Hidden</span>}
      </p>
    </div>
    <div className="sm:col-span-3 flex sm:justify-end gap-2">
      <button onClick={onToggle} data-testid="testimonial-toggle-btn" title={t.published ? "Hide from site" : "Show on site"}
        className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-2 border border-[#C9BFA8] text-ink-muted hover:text-navy hover:border-navy transition-colors">
        {t.published ? <><EyeOff size={12} /> Hide</> : <><Eye size={12} /> Show</>}
      </button>
      <button onClick={onDelete} data-testid="testimonial-delete-btn" aria-label="Delete testimonial"
        className="p-2 border border-[#C9BFA8] text-ink-muted hover:text-red-700 hover:border-red-700 transition-colors">
        <Trash2 size={13} />
      </button>
    </div>
  </li>
);

export const TestimonialsPanel = ({ onAuthError }) => {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const guard = (err) => (err?.response?.status === 401 ? onAuthError() : toast.error(apiError(err)));

  useEffect(() => {
    adminApi.get("/admin/testimonials").then((r) => setItems(r.data)).catch(guard);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const add = async (e) => {
    e.preventDefault();
    if (form.quote.trim().length < 10) { toast.error("Quote should be at least 10 characters."); return; }
    if (!form.author.trim()) { toast.error("Add the client's name."); return; }
    setSaving(true);
    try {
      const { data } = await adminApi.post("/admin/testimonials", form);
      setItems((l) => [data, ...l]);
      setForm(EMPTY);
      toast.success(data.published ? "Testimonial is live on the Home page." : "Testimonial saved (hidden).");
    } catch (err) { guard(err); } finally { setSaving(false); }
  };

  const toggle = async (t) => {
    try {
      const { data } = await adminApi.put(`/admin/testimonials/${t.id}`, { ...t, published: !t.published });
      setItems((l) => l.map((x) => (x.id === t.id ? data : x)));
      toast.success(data.published ? "Now showing on the site." : "Hidden from the site.");
    } catch (err) { guard(err); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete the testimonial from ${t.author}? This cannot be undone.`)) return;
    try {
      await adminApi.delete(`/admin/testimonials/${t.id}`);
      setItems((l) => l.filter((x) => x.id !== t.id));
      toast.success("Testimonial deleted.");
    } catch (err) { guard(err); }
  };

  const liveCount = items?.filter((t) => t.published).length ?? 0;

  return (
    <section data-testid="admin-testimonials-panel" className="bg-cream-ivory border border-[#D9CFB8] p-8 lg:p-10">
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8a6b2f] mb-2">Home page</p>
          <h2 className="font-serif text-3xl text-navy flex items-center gap-3"><Quote size={22} className="text-[#8a6b2f]" /> Client testimonials</h2>
          <p className="text-ink-muted text-sm mt-3 max-w-lg">Only quotes you add here appear on the site. The section stays hidden until at least one is showing.</p>
        </div>
        <div className="text-right">
          <span data-testid="testimonials-live-count" className="font-serif text-4xl text-navy">{items ? liveCount : "—"}</span>
          <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-ink-muted">showing on site</p>
        </div>
      </div>

      <form onSubmit={add} className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-6" noValidate data-testid="testimonial-form">
        <div className="sm:col-span-2">
          <label className={LABEL} htmlFor="t-quote">Client quote</label>
          <textarea id="t-quote" rows={3} maxLength={600} data-testid="testimonial-quote-input" className={`${FIELD} resize-none`} value={form.quote} onChange={update("quote")}
            placeholder="What the client said, in their own words." />
        </div>
        <div>
          <label className={LABEL} htmlFor="t-author">Client name</label>
          <input id="t-author" maxLength={120} data-testid="testimonial-author-input" className={FIELD} value={form.author} onChange={update("author")} placeholder="Jane Doe" />
        </div>
        <div>
          <label className={LABEL} htmlFor="t-business">Business (optional)</label>
          <input id="t-business" maxLength={160} data-testid="testimonial-business-input" className={FIELD} value={form.business} onChange={update("business")} placeholder="Doe Landscaping, Phoenix" />
        </div>
        <div className="sm:col-span-2 flex flex-wrap items-center gap-6">
          <button type="submit" disabled={saving} data-testid="testimonial-add-btn"
            className="inline-flex items-center gap-2 bg-navy text-parchment px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] border border-gold/30 hover:-translate-y-0.5 transition-all disabled:opacity-60">
            <Plus size={14} /> {saving ? "Adding…" : "Add testimonial"}
          </button>
          <label className="flex items-center gap-3 cursor-pointer select-none font-mono text-[10px] uppercase tracking-[0.2em] text-ink">
            <input type="checkbox" data-testid="testimonial-published-input" className="accent-[#0A1128] w-4 h-4" checked={form.published}
              onChange={(e) => setForm((s) => ({ ...s, published: e.target.checked }))} />
            Show on site immediately
          </label>
        </div>
      </form>

      {items && items.length === 0 && <p data-testid="testimonials-empty" className="mt-10 text-ink-muted text-sm">No testimonials yet — add your first one above.</p>}
      {items?.length > 0 && (
        <ul data-testid="admin-testimonial-list" className="mt-10 divide-y divide-[#D9CFB8] border-t border-[#D9CFB8]">
          {items.map((t) => <TestimonialRow key={t.id} t={t} onToggle={() => toggle(t)} onDelete={() => remove(t)} />)}
        </ul>
      )}
    </section>
  );
};
